import { AnimatedContentLoader, AuditTrailStep } from '@root/components'
import UserCommentInput from '@root/components/UserCommentInput/UserCommentInput'
import { useBusinessContext } from '@root/context'
import { fetchPeople, IDocument } from '@root/query'
import {
  createAuditTrailDocumentComment,
  documentAuditTrailVisited,
  fetchDocumentAuditTrail,
  IAuditTrailComment
} from '@root/query/auditTrail'
import theme from '@root/styles/theme'
import { setAPIErrors } from '@root/utils'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'

interface IDocumentAuditTrailProps {
  document: IDocument
}

export const DocumentAuditTrail: React.FC<IDocumentAuditTrailProps> = ({ document }) => {
  const { slug } = useBusinessContext()
  const [t] = useTranslation()

  /**
   * Opening the audit trail marks it as visited, which would immediately clear the "new" tags.
   * Keep the timestamp from mount so the tags stay visible until the user leaves and comes back.
   * Reset only when switching documents, not when audit_trail_last_seen_at updates after visit.
   */
  const [seenAtSnapshot, setSeenAtSnapshot] = useState(() => ({
    documentId: document.id,
    seenAt: document.audit_trail_last_seen_at
  }))

  useEffect(() => {
    setSeenAtSnapshot(current =>
      current.documentId === document.id
        ? current
        : { documentId: document.id, seenAt: document.audit_trail_last_seen_at }
    )
  }, [document.id, document.audit_trail_last_seen_at])

  const seenAt =
    seenAtSnapshot.documentId === document.id
      ? seenAtSnapshot.seenAt
      : document.audit_trail_last_seen_at

  interface IFormValues {
    text: string
  }

  const methods = useForm<IFormValues>({
    defaultValues: {
      text: ''
    }
  })

  const { reset, setError } = methods

  const queryClient = useQueryClient()

  const createMutation = useMutation<IAuditTrailComment, unknown, Partial<IAuditTrailComment>>({
    mutationFn: data => createAuditTrailDocumentComment({ slug, documentId: document.id }, data),
    ...{
      onError: error => {
        const { status, data } = (error as any) ?? {}
        if (status === 400) {
          setAPIErrors(data, setError)
        } else {
          toast.error(t('general.error'))
        }
      },
      onSuccess: () => {
        toast.success(t('components.auditTrail.commentAdded'))
        queryClient.invalidateQueries({ queryKey: queryKeyForAuditTrail(slug, document.id) })
        queryClient.invalidateQueries({ queryKey: [slug, 'documents'] })
        visitAuditTrail()
        reset({ text: '' })
      }
    }
  })

  const {
    data,
    isLoading: isAuditTrailLoading,
    isSuccess: isAuditTrailDataSuccess,
    isError
  } = useQuery({
    queryKey: queryKeyForAuditTrail(slug, document.id),
    queryFn: () => fetchDocumentAuditTrail({ slug, documentId: document.id })
  })

  const {
    data: users,
    isLoading: isUsersLoading,
    isSuccess: isPeopleDataSuccess
  } = useQuery({
    queryKey: [slug, 'people'],
    queryFn: () => fetchPeople({ slug }, { count: 1000 })
  })

  const { mutate: visitAuditTrail } = useMutation({
    mutationFn: () => documentAuditTrailVisited({ slug, documentId: document.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [slug, 'documents'] })
    }
  })

  useEffect(() => {
    if (isAuditTrailDataSuccess && isPeopleDataSuccess) {
      visitAuditTrail()
    }
  }, [isAuditTrailDataSuccess, isPeopleDataSuccess, visitAuditTrail])

  const isLoading = isAuditTrailLoading || isUsersLoading

  const lastAudit = data?.length - 1

  return (
    <AnimatedContentLoader
      isLoading={isLoading}
      isEmpty={isError}
      isEmptyDescription={t('error.oho')}
    >
      <FormProvider {...methods}>
        <UserCommentInput createMutation={createMutation} />
      </FormProvider>

      <AuditTrailWrapper style={{ marginTop: `${theme.spacing.xl}rem` }}>
        {data?.map((audit, index) => (
          <AuditTrailStep
            key={`${audit.time}-${index}-audit-trail-step`}
            isLast={lastAudit === index}
            auditRecord={audit}
            users={users?.results}
            seenAt={seenAt}
          ></AuditTrailStep>
        ))}
      </AuditTrailWrapper>
    </AnimatedContentLoader>
  )
}

export const queryKeyForAuditTrail = (slug: string, documentId: number) => [
  slug,
  'document_audit_trail',
  { documentId }
]

const AuditTrailWrapper = styled.div``
